import React from 'react';
import { useSEO } from '../utils/seo';
import { CUSTOM_ORDERS_ENABLED } from '../lib/shop';
import { SHIPPING_RATES, deliveryDelay } from '../lib/shipping';

// Tarifs et délais lus dans la configuration de livraison partagée avec le serveur.
const euroFr = value => `${value.toFixed(2).replace('.', ',')} €`;
const euroEn = value => `€${value.toFixed(2)}`;
import { useCopy, useLocale } from '../i18n/LocaleContext';

// Identité légale de l'entreprise (immatriculée le 05/08/2026). Les variables d'environnement
// sont intégrées à la compilation du frontend ; toute modification exige un rebuild.
const legalIdentity = {
  publisher: process.env.REACT_APP_LEGAL_PUBLISHER || 'Margot Emery, entrepreneur individuel',
  businessStatus: process.env.REACT_APP_LEGAL_STATUS || 'Entrepreneur individuel (micro-entreprise) — fabrication d’articles textiles',
  siret: process.env.REACT_APP_LEGAL_SIRET || '108 484 692 00018',
  vat: process.env.REACT_APP_LEGAL_VAT || 'TVA non applicable, art. 293 B du CGI',
  email: process.env.REACT_APP_CONTACT_EMAIL || 'margot@le-placard-a-doudous.com',
  phone: process.env.REACT_APP_LEGAL_PHONE || '06 07 28 71 15',
  // Adresse de domiciliation à renseigner (obligatoire pour une entreprise individuelle qui vend en ligne).
  address: process.env.REACT_APP_LEGAL_ADDRESS || '11 rue Angélys, 35250 Saint-Médard-sur-Ille, France',
  // Source : https://railway.com/legal/terms (coordonnées publiques, octobre 2026).
  host: process.env.REACT_APP_LEGAL_HOST || 'Railway Corporation — 548 Market St, Suite 68956, San Francisco, CA 94104, États-Unis — +1 415 707 7675 — team@railway.com — https://railway.com',
  mediator: process.env.REACT_APP_LEGAL_MEDIATOR || 'Médiateur de la consommation à désigner avant ouverture publique',
};

const legal = {
  'mentions-legales': {
    title: 'Mentions Légales',
    content: `
## Éditeur du site

Dénomination : Le Placard à Doudous
Éditrice et directrice de la publication : ${legalIdentity.publisher}
Statut : ${legalIdentity.businessStatus}
SIRET : ${legalIdentity.siret}
${legalIdentity.vat}
Email : ${legalIdentity.email}
Téléphone : ${legalIdentity.phone}
${legalIdentity.address}
Pays : France

## Hébergement

${legalIdentity.host}

## Propriété intellectuelle

L’ensemble du contenu de ce site (textes, images, créations) est la propriété exclusive de Le Placard à Doudous. Toute reproduction est interdite sans autorisation.

## Médiation de la consommation

Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, le client peut recourir gratuitement à un médiateur de la consommation en cas de litige non résolu. Médiateur : ${legalIdentity.mediator}.
	    `
  },
  'cgv': {
    title: 'Conditions Générales de Vente',
    content: `
## Article 1 — Objet

Les présentes CGV régissent les ventes conclues entre ${legalIdentity.publisher} (SIRET ${legalIdentity.siret}), exerçant sous la dénomination Le Placard à Doudous, et tout client du site. Les produits sont des marque-pages en tissu cousus main, ${CUSTOM_ORDERS_ENABLED ? 'vendus soit en pièce unique disponible immédiatement, soit cousus sur mesure à la commande.' : 'vendus en pièces uniques disponibles immédiatement.'}

## Article 2 — Prix

Les prix sont indiqués en euros. ${legalIdentity.vat}. Les frais de livraison sont indiqués avant la validation de la commande. Livraison offerte dès 59 € d’achats.

## Article 3 — Commande

Toute commande vaut acceptation des présentes CGV. Le paiement est sécurisé par Stripe. La commande est confirmée après validation du paiement.

${CUSTOM_ORDERS_ENABLED ? `## Article 3 bis — Pièces sur mesure

Une pièce sur mesure est cousue à la commande, selon les choix du client (couleurs du Minky, matière du dos, tissu du ruban et fil de couture). Le visage brodé reste celui du modèle. Le délai de confection est de 10 à 15 jours avant expédition. Confectionnée selon les spécifications du client, une pièce sur mesure est exclue du droit de rétractation (article L221-28, 3° du Code de la consommation). Le client en est informé avant de valider sa commande.

L’aperçu affiché dans le configurateur est une simulation non contractuelle : chaque pièce étant cousue et brodée à la main, la pièce livrée peut présenter de légères différences de teinte, de placement des motifs ou de broderie. Avant l’expédition, l’atelier envoie au client des photos de sa pièce terminée.
` : ''}
## Article 4 — Livraison

${CUSTOM_ORDERS_ENABLED ? 'Les pièces disponibles sont expédiées sous 48 h ouvrées ; les pièces sur mesure après confection (10 à 15 jours).' : 'Les pièces sont expédiées sous 48 h ouvrées.'} En cas de retard exceptionnel, le client est informé par email.

Les commandes sont livrées en France et dans le monde entier, à l’exception des États-Unis et des pays où La Poste ne distribue pas le courrier ; les pays desservis sont proposés lors de la commande. Pour les livraisons hors de l’Union européenne, les taxes d’importation, droits de douane et frais de dédouanement éventuels sont à la charge du destinataire.

## Article 5 — Droit de rétractation

${CUSTOM_ORDERS_ENABLED ? 'Pour les pièces de la collection, vous disposez' : 'Vous disposez'} de 14 jours à compter de la réception pour exercer votre droit de rétractation, sans justification.

**Frais de retour :** les frais de renvoi de la pièce sont entièrement à la charge de l’acheteur.

**État de la pièce :** la pièce doit être retournée neuve, non utilisée, non lavée, complète et dans l’état exact où elle a été expédiée, si possible dans son emballage d’origine. Vous pouvez l’examiner comme en boutique pour en apprécier la nature et les caractéristiques ; toute utilisation ou manipulation allant au-delà engage votre responsabilité et entraîne une réduction du remboursement à hauteur de la dépréciation constatée, pouvant aller jusqu’à la totalité du prix de la pièce.

${CUSTOM_ORDERS_ENABLED ? `Les pièces sur mesure, confectionnées selon vos choix, ne bénéficient pas du droit de rétractation (article L221-28, 3° du Code de la consommation). Elles restent couvertes par les garanties légales de l’article 6.

` : ''}Pour exercer ce droit, écrivez à ${legalIdentity.email} avec votre numéro de commande, ou utilisez le formulaire ci-dessous. Après nous avoir informés, vous avez 14 jours pour renvoyer la pièce. Le remboursement porte sur le prix de la pièce et les frais de livraison initiaux au tarif standard, comme l’exige la loi (un mode de livraison plus coûteux choisi par l’acheteur n’est remboursé qu’à hauteur du tarif standard). Il intervient au plus tard 14 jours après votre demande ; il peut être différé jusqu’à la réception du bien ou d’une preuve d’expédition, la première de ces dates étant retenue.

## Article 5 bis — Formulaire de rétractation

À recopier dans un email adressé à ${legalIdentity.email} :

« Je vous notifie par la présente ma rétractation du contrat portant sur la vente du bien ci-dessous.
Commandé le : ____ / Reçu le : ____
Numéro de commande : ____
Nom du client : ____
Adresse du client : ____
Date : ____ »

## Article 6 — Garanties

Chaque produit est contrôlé avant expédition. Vous bénéficiez des garanties légales de conformité et des vices cachés. La garantie légale de conformité peut être exercée pendant deux ans à compter de la délivrance du bien, sans frais pour le consommateur. Elle permet, selon les conditions légales, la réparation ou le remplacement du produit et, dans les cas prévus par la loi, une réduction du prix ou la résolution de la vente. La garantie des vices cachés s’exerce dans les deux ans à compter de la découverte du vice. Contactez ${legalIdentity.email} pour toute demande ; le délai de rétractation de 14 jours ne limite pas ces garanties.${CUSTOM_ORDERS_ENABLED ? ' Elles s’appliquent aussi aux pièces sur mesure.' : ''}

**Ce que couvre la garantie de conformité :** une pièce qui ne correspond pas à sa description ou à la commande (tissu, couleur, dimensions), qui ne permet pas un usage normal de marque-page (couture qui lâche, broderie qui se défait, ruban qui se détache lors d’un usage normal) ou qui arrive endommagée.

**Ce qui n’est pas couvert :** l’usure normale liée à l’usage (notamment le poil du minky qui s’aplatit avec le temps) ; les dommages résultant d’un entretien non conforme aux consignes indiquées (lavage en machine, sèche-linge, séchage suspendu) ; les dommages dus à un accident, une mauvaise utilisation ou une négligence (arrachement, morsure d’animal, objet coincé, exposition à une source de chaleur) ; les pièces modifiées ou réparées par un tiers.

**Caractère fait main :** chaque pièce est cousue et brodée à la main. De légères variations de forme, de placement du motif, de broderie ou de teinte d’une pièce à l’autre ou par rapport aux photos sont inhérentes à ce mode de fabrication et ne constituent pas un défaut de conformité.

## Article 7 — Médiation

En cas de litige, contactez d’abord ${legalIdentity.email}. À défaut de solution amiable, vous pouvez saisir le médiateur indiqué dans les mentions légales.
	    `
  },
  'confidentialite': {
    title: 'Politique de Confidentialité',
    content: `
## Collecte des données

Nous collectons uniquement les données nécessaires au traitement de vos commandes : nom, email, adresse de livraison. Aucune donnée bancaire n’est stockée sur nos serveurs (paiement géré par Stripe).

## Utilisation

Vos données sont utilisées exclusivement pour : traiter vos commandes, vous envoyer des confirmations, gérer le service client, sécuriser le paiement et respecter les obligations comptables. Nous ne vendons jamais vos données à des tiers.

## Vos droits

Conformément au RGPD, vous disposez d’un droit d’accès, de rectification, d’opposition, de limitation et de suppression de vos données. Contactez-nous : ${legalIdentity.email}

## Cookies

Nous utilisons uniquement les cookies nécessaires au fonctionnement du site (session, panier, sécurité du paiement). Aucun cookie publicitaire n’est déposé sans consentement.
	    `
  },
  'livraison': {
    title: 'Livraison & Retours',
    content: `
## Livraison

**Délai :** ${CUSTOM_ORDERS_ENABLED ? 'pièce disponible expédiée sous 48 h ouvrées ; pièce sur mesure cousue puis expédiée sous 10 à 15 jours.' : 'expédition sous 48 h ouvrées.'}
**Tarif :** ${euroFr(SHIPPING_RATES.FR)} en France métropolitaine et à Monaco, ${euroFr(SHIPPING_RATES.EU)} dans l’Union européenne, ${euroFr(SHIPPING_RATES.WORLD)} pour le reste du monde et l’outre-mer (pas de livraison aux États-Unis pour l’instant). Offerte dès 59 € d’achats, quelle que soit la destination.
**Délai de livraison après expédition :** France, ${deliveryDelay('FR')} ; Union européenne, ${deliveryDelay('EU')} ; hors Union européenne, ${deliveryDelay('WORLD')}.
**Transporteur :** La Poste, en envoi suivi (Lettre Suivie ou Colissimo selon le poids).

**Hors Union européenne :** une déclaration de douane accompagne le colis. Taxes d’import éventuelles à la charge du destinataire : le pays de destination peut demander des taxes, droits de douane ou frais de dédouanement à la réception.

Chaque commande est soigneusement emballée dans du papier de soie, avec un petit mot manuscrit de Margot.

## Suivi de commande

Un email de confirmation avec numéro de suivi vous est envoyé dès l’expédition.

## Retours

**Délai :** 14 jours après réception${CUSTOM_ORDERS_ENABLED ? ' pour les pièces de la collection' : ''}, conformément à nos CGV.
${CUSTOM_ORDERS_ENABLED ? `**Pièces sur mesure :** confectionnées selon vos choix, elles ne peuvent être ni reprises ni échangées, sauf défaut de conformité couvert par la garantie légale (voir nos CGV, article 6).
` : ''}**Conditions :** La pièce doit être retournée neuve, non utilisée, non lavée et dans l’état exact où elle a été expédiée, bien protégée et si possible dans son emballage d’origine. Une pièce utilisée ou abîmée donne lieu à une réduction du remboursement à hauteur de la dépréciation constatée.
**Frais de retour :** Entièrement à la charge de l’acheteur, sauf défaut de conformité couvert par la garantie légale.

Pour initier un retour : ${legalIdentity.email}
	    `
  }
};

// Traduction anglaise, à titre informatif : la version française fait foi.
const identityEn = {
  businessStatus: process.env.REACT_APP_LEGAL_STATUS_EN || 'Sole trader (French micro-enterprise) — manufacture of textile goods',
  vat: process.env.REACT_APP_LEGAL_VAT_EN || 'VAT not applicable, article 293 B of the French General Tax Code',
  mediator: process.env.REACT_APP_LEGAL_MEDIATOR_EN || (process.env.REACT_APP_LEGAL_MEDIATOR ? legalIdentity.mediator : 'Consumer mediator to be appointed before the public opening'),
};

const legalEn = {
  'mentions-legales': {
    title: 'Legal Notice',
    content: `
## Website publisher

Trading name: Le Placard à Doudous
Publisher and publication director: ${legalIdentity.publisher}
Status: ${identityEn.businessStatus}
SIRET: ${legalIdentity.siret}
${identityEn.vat}
Email: ${legalIdentity.email}
Phone: ${legalIdentity.phone}
${legalIdentity.address}
Country: France

## Hosting

${legalIdentity.host}

## Intellectual property

All content on this website (texts, images, designs) is the exclusive property of Le Placard à Doudous. Any reproduction is prohibited without permission.

## Consumer mediation

In accordance with articles L.616-1 and R.616-1 of the French Consumer Code, customers may use a consumer mediator free of charge in the event of an unresolved dispute. Mediator: ${identityEn.mediator}.
	    `
  },
  'cgv': {
    title: 'Terms and Conditions of Sale',
    content: `
## Article 1 — Purpose

These terms and conditions govern sales concluded between ${legalIdentity.publisher} (SIRET ${legalIdentity.siret}), trading as Le Placard à Doudous, and any customer of the website. The products are fabric bookmarks sewn by hand, ${CUSTOM_ORDERS_ENABLED ? 'sold either as one-of-a-kind pieces available immediately, or sewn to order.' : 'sold as one-of-a-kind pieces available immediately.'}

## Article 2 — Prices

Prices are shown in euros. ${identityEn.vat}. Shipping costs are shown before the order is confirmed. Free shipping on orders of €59 or more.

## Article 3 — Orders

Placing an order constitutes acceptance of these terms and conditions. Payment is secured by Stripe. The order is confirmed once payment has been approved.

${CUSTOM_ORDERS_ENABLED ? `## Article 3a — Made-to-order pieces

A made-to-order piece is sewn to order according to the customer's choices (Minky colours, back fabric, ribbon fabric and sewing thread). The embroidered face remains that of the original model. Making takes 10 to 15 days before shipping. Because it is made to the customer's specifications, a made-to-order piece is excluded from the right of withdrawal (article L221-28, 3° of the French Consumer Code). The customer is informed of this before confirming the order.

The preview shown in the configurator is a non-contractual simulation: as each piece is sewn and embroidered by hand, the delivered piece may show slight differences in colour, pattern placement or embroidery. Before shipping, the workshop sends the customer photos of the finished piece.
` : ''}
## Article 4 — Delivery

${CUSTOM_ORDERS_ENABLED ? 'Available pieces are shipped within 2 working days; made-to-order pieces after they are made (10 to 15 days).' : 'Pieces are shipped within 2 working days.'} In the event of an exceptional delay, the customer is informed by email.

Orders are delivered to France and worldwide, except to the United States and to countries where La Poste does not deliver mail; the countries served are listed at checkout. For deliveries outside the European Union, any import taxes, customs duties and clearance fees are payable by the recipient.

## Article 5 — Right of withdrawal

${CUSTOM_ORDERS_ENABLED ? 'For pieces from the collection, you have' : 'You have'} 14 days from receipt to exercise your right of withdrawal, without having to give a reason.

**Return costs:** the cost of sending the piece back is borne entirely by the buyer.

**Condition of the piece:** the piece must be returned new, unused, unwashed, complete and in exactly the condition in which it was shipped, in its original packaging if possible. You may examine it as you would in a shop to assess its nature and characteristics; any use or handling beyond that is your responsibility and will lead to a reduction of the refund in line with the loss of value observed, which may amount to the full price of the piece.

${CUSTOM_ORDERS_ENABLED ? `Made-to-order pieces, made according to your choices, are not covered by the right of withdrawal (article L221-28, 3° of the French Consumer Code). They remain covered by the legal guarantees in article 6.

` : ''}To exercise this right, write to ${legalIdentity.email} with your order number, or use the form below. After informing us, you have 14 days to send the piece back. The refund covers the price of the piece and the original shipping costs at the standard rate, as required by law (a more expensive delivery method chosen by the buyer is only refunded up to the standard rate). It is made no later than 14 days after your request; it may be withheld until the item, or proof that it has been sent back, is received, whichever comes first.

## Article 5a — Withdrawal form

To copy into an email to ${legalIdentity.email}:

“I hereby give notice that I withdraw from my contract for the sale of the following item.
Ordered on: ____ / Received on: ____
Order number: ____
Customer name: ____
Customer address: ____
Date: ____”

## Article 6 — Guarantees

Every product is checked before shipping. You benefit from the legal guarantees of conformity and against hidden defects. The legal guarantee of conformity can be claimed for two years from delivery of the item, at no cost to the consumer. Under the legal conditions, it allows the product to be repaired or replaced and, in the cases provided for by law, a price reduction or cancellation of the sale. The guarantee against hidden defects can be claimed within two years of discovering the defect. Contact ${legalIdentity.email} for any request; the 14-day withdrawal period does not limit these guarantees.${CUSTOM_ORDERS_ENABLED ? ' They also apply to made-to-order pieces.' : ''}

**What the guarantee of conformity covers:** a piece that does not match its description or the order (fabric, colour, dimensions), that cannot be used normally as a bookmark (a seam that gives way, embroidery that comes undone, a ribbon that comes off during normal use) or that arrives damaged.

**What is not covered:** normal wear and tear from use (in particular minky pile that flattens over time); damage resulting from care that does not follow the instructions given (machine washing, tumble drying, hanging to dry); damage caused by an accident, misuse or negligence (tearing, animal bites, trapped objects, exposure to a heat source); pieces modified or repaired by a third party.

**Handmade nature:** each piece is sewn and embroidered by hand. Slight variations in shape, pattern placement, embroidery or colour from one piece to another, or compared with the photos, are inherent to this way of making and do not constitute a lack of conformity.

## Article 7 — Mediation

In the event of a dispute, please contact ${legalIdentity.email} first. If no amicable solution is found, you may refer the matter to the mediator named in the legal notice.
	    `
  },
  'confidentialite': {
    title: 'Privacy Policy',
    content: `
## Data collection

We only collect the data needed to process your orders: name, email, delivery address. No bank details are stored on our servers (payment is handled by Stripe).

## Use

Your data is used exclusively to: process your orders, send you confirmations, provide customer service, secure payment and meet our accounting obligations. We never sell your data to third parties.

## Your rights

Under the GDPR, you have the right to access, rectify, object to, restrict and erase your data. Contact us: ${legalIdentity.email}

## Cookies

We only use the cookies needed for the website to work (session, shopping bag, payment security). No advertising cookies are set without your consent.
	    `
  },
  'livraison': {
    title: 'Shipping & Returns',
    content: `
## Shipping

**Lead time:** ${CUSTOM_ORDERS_ENABLED ? 'available pieces shipped within 2 working days; made-to-order pieces sewn and then shipped within 10 to 15 days.' : 'shipped within 2 working days.'}
**Rate:** ${euroEn(SHIPPING_RATES.FR)} for mainland France and Monaco, ${euroEn(SHIPPING_RATES.EU)} within the European Union, ${euroEn(SHIPPING_RATES.WORLD)} for the rest of the world and French overseas territories (no delivery to the United States for now). Free on orders of €59 or more, wherever you are.
**Delivery time after shipping:** France, ${deliveryDelay('FR', 'en')}; European Union, ${deliveryDelay('EU', 'en')}; outside the European Union, ${deliveryDelay('WORLD', 'en')}.
**Carrier:** La Poste, tracked (Lettre Suivie or Colissimo depending on weight).

**Outside the European Union:** a customs declaration travels with the parcel. Any import taxes are payable by the recipient: the destination country may charge taxes, customs duties or clearance fees on delivery.

Each order is carefully wrapped in tissue paper, with a small handwritten note from Margot.

## Order tracking

A confirmation email with a tracking number is sent to you as soon as your order ships.

## Returns

**Time limit:** 14 days after receipt${CUSTOM_ORDERS_ENABLED ? ' for pieces from the collection' : ''}, in accordance with our terms and conditions of sale.
${CUSTOM_ORDERS_ENABLED ? `**Made-to-order pieces:** made according to your choices, they cannot be returned or exchanged, except for a lack of conformity covered by the legal guarantee (see our terms and conditions, article 6).
` : ''}**Conditions:** The piece must be returned new, unused, unwashed and in exactly the condition in which it was shipped, well protected and in its original packaging if possible. A used or damaged piece will lead to a reduction of the refund in line with the loss of value observed.
**Return costs:** Borne entirely by the buyer, except for a lack of conformity covered by the legal guarantee.

To start a return: ${legalIdentity.email}
	    `
  }
};

const COPY = {
  fr: {
    fallbackTitle: 'Page Légale — Le Placard à Doudous', fallbackDescription: 'Informations légales liées à la boutique Le Placard à Doudous.',
    description: title => `Lisez les informations concernant : ${title}.`, updated: 'Dernière mise à jour : septembre 2026 — Le Placard à Doudous',
  },
  en: {
    fallbackTitle: 'Legal information — Le Placard à Doudous', fallbackDescription: 'Legal information about the Le Placard à Doudous shop.',
    description: title => `Read the information about: ${title}.`, updated: 'Last updated: September 2026 — Le Placard à Doudous',
    notice: 'This English translation is provided for information only. The French version is the only legally binding version.',
  },
};

export default function LegalPage({ page }) {
  const c = useCopy(COPY);
  const { isEnglish } = useLocale();
  const content = (isEnglish ? legalEn : legal)[page];

  useSEO(
    content ? `${content.title} — Le Placard à Doudous` : c.fallbackTitle,
    content ? c.description(content.title) : c.fallbackDescription
  );

  if (!content) return null;

  // Simple markdown-like rendering
  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="font-serif text-xl text-foreground mt-8 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-sans text-sm font-semibold text-[hsl(var(--foreground))] my-1">{line.replace(/\*\*/g, '')}</p>;
      if (line.includes('**')) {
        const parts = line.split('**');
        return <p key={i} className="font-sans text-sm text-[hsl(var(--muted-foreground))] leading-relaxed my-1">{parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-[hsl(var(--foreground))]">{p}</strong> : p)}</p>;
      }
      if (line === '') return <div key={i} className="my-2" />;
      return <p key={i} className="font-sans text-sm text-[hsl(var(--muted-foreground))] leading-relaxed my-1">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen pt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h1 className="font-serif text-3xl sm:text-4xl text-foreground mb-10">{content.title}</h1>
        {c.notice && <p className="-mt-6 mb-8 font-sans text-xs italic text-muted-foreground">{c.notice}</p>}
        <div className="rounded-2xl bg-card shadow-soft p-6 sm:p-8">
          {renderContent(content.content)}
        </div>
        <p className="font-sans text-xs text-[hsl(var(--muted-foreground))] mt-6 text-center">
          {c.updated}
        </p>
      </div>
    </div>
  );
}
